import { useState, useEffect } from "react";
import styles from "../../styles/addReceipt.module.css";
import { formatCurrency } from "../../../../../shared/utils/formatCurrency";

/**
 * CxcSummary
 *
 * Muestra el resumen de abonos y el restante en la moneda del banco.
 *
 * Props:
 *   facturasData — array de CxC con .abono en moneda original de cada factura
 *   restante     — restante en CRC (calculado por useCxc)
 *   bancoMoneda  — 'CRC' | 'USD'
 *   TC_compra    — tipo de cambio de compra
 */
export function CxcSummary({ facturasData, restante, bancoMoneda = "CRC", TC_compra = 1 }) {
  const [totalCRC, setTotalCRC]   = useState(0);
  const [totalUSD, setTotalUSD]   = useState(0);
  const [favorCRC, setFavorCRC]   = useState(0);
  const [favorUSD, setFavorUSD]   = useState(0);
  const [comisionBanco, setComisionBanco] = useState(0);

  const tc = Number(TC_compra) || 1;

  useEffect(() => {
    let sumCRC = 0, sumUSD = 0, favCRC = 0, favUSD = 0;
    let comisionCRC = 0, comisionUSD = 0;
    for (const inv of facturasData) {
      const comision = Number(inv.comision_bancaria) || 0;
      if (comision > 0) {
        if (inv.moneda === "USD") {
          comisionUSD += comision;
          comisionCRC += comision * tc;
        } else {
          comisionCRC += comision;
          comisionUSD += comision / tc;
        }
      }
      if (Number(inv.abono) <= 0) continue;
      const fav      = Number(inv.favor)      || 0;
      const abono    = Number(inv.abono);
      const saldoInv = Math.abs(Number(inv.saldo) || 0);
      if (inv.moneda === "USD") {
        sumUSD += abono;
        // Si distribute/updateChecked grabó el costo CRC real, úsalo directamente
        // (saldo a favor USD → factura USD: el CRC real es abono × TC_venta_del_crédito).
        if (inv.abono_crc_cost !== undefined) {
          sumCRC += inv.abono_crc_cost;
        } else if (bancoMoneda === "CRC" && Number(inv.tipo_cambio) > 1 && abono >= saldoInv - 0.005) {
          // Banco CRC + factura USD, pago completo → acredita a TC_venta
          sumCRC += abono * (Number(inv.tipo_cambio) || tc);
        } else {
          sumCRC += abono * tc;           // TC_compra (caso estándar)
        }
        favUSD += fav;
        favCRC += fav * tc;
      } else {
        sumCRC += abono;
        sumUSD += abono / tc;
        favCRC += fav;
        favUSD += fav / tc;
      }
    }
    setTotalCRC(Math.round(sumCRC * 100) / 100);
    setTotalUSD(Math.round(sumUSD * 100) / 100);
    setFavorCRC(Math.round(favCRC * 100) / 100);
    setFavorUSD(Math.round(favUSD * 100) / 100);
    setComisionBanco(Math.round((bancoMoneda === "USD" ? comisionUSD : comisionCRC) * 100) / 100);
  }, [facturasData, TC_compra, tc, bancoMoneda]);

  // restante viene en CRC desde useCxc; convertir a moneda del banco
  const restanteBanco = bancoMoneda === "USD"
    ? Math.round((restante / tc) * 100) / 100
    : restante;

  const restanteColor =
    restanteBanco < 0 ? "#4ade80" :
    restanteBanco > 0 ? "#f97316" :
    "#4ade80";

  const restanteLabel =
    restanteBanco < 0 ? "Saldo a favor" :
    restanteBanco > 0 ? "Total Restante" :
    "Pagado completo ✓";

  const monedaSimbolo = bancoMoneda === "USD" ? "$" : "₡";

  // Display total in bank currency
  const totalBanco    = bancoMoneda === "USD" ? totalUSD    : totalCRC;
  const favorBanco    = bancoMoneda === "USD" ? favorUSD    : favorCRC;

  function fmt(n) {
    if (bancoMoneda === "USD") return Number(n).toFixed(2);
    return formatCurrency(n);
  }

  return (
    <div className={styles["cxc-details-summary"]}>
      <div className={styles["cxc-summary-relevant"]}>
        <h2>RESUMEN RÁPIDO</h2>
        <div className="d-flex justify-content-between align-items-center">
          <span>Total CxC seleccionado</span>
          <span style={{ color: bancoMoneda === "USD" ? "#60a5fa" : undefined }}>
            {monedaSimbolo} {fmt(totalBanco)}
          </span>
        </div>
        {favorBanco > 0 && (
          <div className="d-flex justify-content-between align-items-center">
            <span>Total Saldo a Favor</span>
            <span style={{ color: "#fb923c" }}>
              {monedaSimbolo} {fmt(favorBanco)}
            </span>
          </div>
        )}
        {comisionBanco > 0 && (
          <div className="d-flex justify-content-between align-items-center">
            <span>Comisión Bancaria (Tarjeta)</span>
            <span style={{ color: "#fcd34d" }}>
              {monedaSimbolo} {fmt(comisionBanco)}
            </span>
          </div>
        )}
        {/* Si hay mix de monedas, mostrar también en CRC */}
        {bancoMoneda === "CRC" && totalUSD > 0 && (
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 4 }}>
            incluye ${totalUSD.toFixed(2)} USD ≈ ₡{formatCurrency(totalCRC)} al TC factura
          </div>
        )}
      </div>
      <div className={styles["cxc-summary-important"]}>
        <h2>{restanteLabel}</h2>
        <span style={{ color: restanteColor, fontVariantNumeric: "tabular-nums" }}>
          {monedaSimbolo} {fmt(Math.abs(restanteBanco))}
        </span>
      </div>
    </div>
  );
}
